import { createContext, useContext, useEffect, useState } from 'react';
import { apiClient, AUTH_BASE } from '../api';
import { PERMISSION_PAGES_MAP } from '../config/permissions';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [pageAccess, setPageAccess] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = async () => {
    try {
      const res = await apiClient.get(`${AUTH_BASE}/api/auth/my-permissions`);
      const perms = res.data.permissions || [];
      setPermissions(perms);
      const pages = [];
      perms.forEach(p => {
        const mapped = PERMISSION_PAGES_MAP[p];
        if (mapped) pages.push(...mapped);
      });
      setPageAccess([...new Set(pages)]);
    } catch {
      setPermissions([]);
      setPageAccess([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const res = await apiClient.get(`${AUTH_BASE}/api/me`);
        setUser(res.data.user ?? res.data);
        setIsAuthenticated(true);
        await fetchPermissions();
      } catch {
        setUser(null);
        setPermissions([]);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const login = () => {
    window.location.href = `${AUTH_BASE}/auth/login`;
  };

  const logout = async () => {
    localStorage.removeItem('auth_token');
    setUser(null);
    setPermissions([]);
    setPageAccess([]);
    setIsAuthenticated(false);
    window.location.href = `${AUTH_BASE}/auth/logout?t=${Date.now()}`;
  };

  const switchOrg = async (tenantId) => {
    try {
      const res = await apiClient.post(`${AUTH_BASE}/api/organizations/${tenantId}/switch`);
      // Update localStorage so Bearer token reflects the new tenant on next request
      if (res.data?.token) {
        localStorage.setItem('auth_token', res.data.token);
      }
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch organization:', error);
      if (error.response?.status === 403) {
        window.location.href = '/';
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, permissions, pageAccess, isAuthenticated, loading, login, logout, switchOrg }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
